import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import PublicLayout from '../../components/public/PublicLayout';
import PageHero from '../../components/public/PageHero';
import { PHONE_DISPLAY, SUPPORT_EMAIL, withBase } from '../../publicSite/config';
import { loadCart, removeFromCart, updateCartQuantity, clearCart, cartTotal } from '../../publicSite/cart';
import { createPayFastCheckout, redirectToPayFast, waitForPayFastPayment } from '../../utils/payfastHostedCheckout';

export default function Cart() {
  const location = useLocation();
  const [items, setItems] = useState(() => loadCart());
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState('');
  const [paid, setPaid] = useState(false);
  const [deliveryOption, setDeliveryOption] = useState('collection');
  const [shippingAddress, setShippingAddress] = useState({
    fullName: '',
    phone: '',
    addressLine1: '',
    city: '',
    postalCode: '',
    country: 'South Africa'
  });
  const DELIVERY_AMOUNT = 150;

  const updateShippingAddress = (field, value) => {
    setShippingAddress((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    const onUpdate = () => setItems(loadCart());
    window.addEventListener('shem-cart-updated', onUpdate);
    return () => window.removeEventListener('shem-cart-updated', onUpdate);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('payment') === 'success') {
      const confirmCartPayment = async () => {
        try {
          const context = JSON.parse(localStorage.getItem('shem_pending_checkout_context') || '{}');
          const checkoutId = String(params.get('orderId') || context?.checkoutId || '');
          if (!checkoutId) {
            setError('Missing checkout reference. Please contact support if you were charged.');
            return;
          }
          await waitForPayFastPayment(checkoutId);
          setPaid(true);
          const pending = JSON.parse(localStorage.getItem('shem_pending_checkout') || '[]');
          const existing = JSON.parse(localStorage.getItem('shem_purchased_products') || '[]');
          if (Array.isArray(pending) && pending.length > 0) {
            const nextPurchased = [...(Array.isArray(existing) ? existing : []), ...pending];
            localStorage.setItem('shem_purchased_products', JSON.stringify(nextPurchased));
          }
          localStorage.removeItem('shem_pending_checkout');
          localStorage.removeItem('shem_pending_checkout_context');
          clearCart();
          setItems([]);
        } catch (statusError) {
          setError(statusError?.message || 'Payment confirmation is still pending.');
        }
      };

      confirmCartPayment();
    }
    if (params.get('payment') === 'cancel') {
      localStorage.removeItem('shem_pending_checkout');
      localStorage.removeItem('shem_pending_checkout_context');
    }
  }, [location.search]);

  const subtotal = useMemo(() => cartTotal(items), [items]);
  const hasPhysicalProducts = useMemo(
    () => items.some((item) => (item.format || 'physical') !== 'pdf'),
    [items]
  );

  useEffect(() => {
    if (!hasPhysicalProducts) {
      setDeliveryOption('collection');
    }
  }, [hasPhysicalProducts]);

  const deliveryFee = useMemo(() => {
    if (!hasPhysicalProducts) return 0;
    return deliveryOption === 'delivery' ? DELIVERY_AMOUNT : 0;
  }, [deliveryOption, hasPhysicalProducts]);

  const total = useMemo(() => subtotal + deliveryFee, [subtotal, deliveryFee]);

  const handleCheckout = async () => {
    setError('');
    if (!items.length || total <= 0) {
      setError('Your cart is empty.');
      return;
    }
    if (hasPhysicalProducts) {
      const requiredFields = ['fullName', 'phone', 'addressLine1', 'city', 'postalCode', 'country'];
      const missing = requiredFields.some((field) => !String(shippingAddress[field] || '').trim());
      if (missing) {
        setError('Please complete your delivery address for physical products.');
        return;
      }
    }
    setCheckoutLoading(true);
    try {
      const checkoutId = `chk-${Date.now()}`;
      const checkoutItems = items.map((item) => ({
        id: item.id,
        productId: item.productId,
        facultySlug: item.facultySlug,
        title: item.title,
        quantity: item.quantity || 1,
        price: item.price,
        format: item.format || 'physical',
        pdfUrl: item.pdfUrl || ''
      }));
      localStorage.setItem('shem_pending_checkout', JSON.stringify(checkoutItems));
      localStorage.setItem(
        'shem_pending_checkout_context',
        JSON.stringify({
          checkoutId,
          subtotal: Number(subtotal.toFixed(2)),
          deliveryFee: Number(deliveryFee.toFixed(2)),
          total: Number(total.toFixed(2)),
          deliveryOption,
          hasPhysicalProducts,
          shippingAddress: hasPhysicalProducts ? shippingAddress : null
        })
      );

      const [firstName = '', ...restName] = String(shippingAddress.fullName || '').trim().split(/\s+/);
      const auth = getAuth();
      const currentUser = auth.currentUser;
      const checkout = await createPayFastCheckout({
        amount: total,
        itemName: 'School of Shem Store Order',
        itemDescription: checkoutItems.map((item) => item.title).join(', ').slice(0, 255),
        successPath: `/cart?payment=success&orderId=${encodeURIComponent(checkoutId)}`,
        cancelPath: `/cart?payment=cancel&orderId=${encodeURIComponent(checkoutId)}`,
        metadata: {
          orderId: checkoutId,
          type: 'faculty_cart',
          source: 'faculty_store',
          userId: currentUser?.uid || null,
          items: checkoutItems,
          total: Number(total.toFixed(2)),
          subtotal: Number(subtotal.toFixed(2)),
          deliveryFee: Number(deliveryFee.toFixed(2)),
          deliveryOption,
          hasPhysicalProducts,
          shippingAddress: hasPhysicalProducts ? shippingAddress : null
        },
        customer: {
          firstName,
          lastName: restName.join(' '),
          phone: shippingAddress.phone || '',
          email: currentUser?.email || ''
        }
      });
      redirectToPayFast(checkout);
    } catch {
      setError('Unable to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <PublicLayout>
      <PageHero
        eyebrow="Store"
        title="Your Cart"
        subtitle="Review your selected books and courses, then complete checkout with Payfast."
        actions={(
          <Link to={withBase('/academics/faculties')} className="btn btn-outline">
            Continue Shopping
          </Link>
        )}
      />

      <section className="ps-section">
        <div className="ps-section-inner">
          {paid && (
            <div className="ps-card" style={{ marginBottom: '16px', border: '1px solid rgba(18,130,60,0.2)' }}>
              <div className="badge badge-success" style={{ marginBottom: '10px' }}>
                Payment successful
              </div>
              <h3 style={{ marginBottom: '8px' }}>Thank you for your payment.</h3>
              <p style={{ color: 'var(--gray-600)', marginBottom: '6px' }}>
                Your order has been confirmed. If your order includes PDF products, they are now unlocked on the faculty page.
              </p>
              <p style={{ color: 'var(--gray-600)', marginBottom: '0' }}>
                Need help? Contact us at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> or {PHONE_DISPLAY}.
              </p>
            </div>
          )}
          {items.length === 0 ? (
            <div className="ps-empty-state">Your cart is empty.</div>
          ) : (
            <div className="ps-card" style={{ display: 'grid', gap: '12px' }}>
              {items.map((item) => (
                <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '88px 1fr auto', gap: '12px', alignItems: 'center', border: '1px solid var(--gray-100)', borderRadius: '12px', padding: '10px' }}>
                  <div style={{ width: '88px', height: '88px', borderRadius: '10px', overflow: 'hidden', background: 'var(--gray-100)' }}>
                    <img src={item.image || '/School Of SHEM Welcome.png'} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700 }}>{item.title}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>{item.facultyTitle || 'Faculty product'}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)', textTransform: 'capitalize' }}>
                      Format: {item.format || 'physical'}
                    </div>
                    <div style={{ marginTop: '6px', fontWeight: 600 }}>R{Number(item.price || 0).toLocaleString('en-ZA')}</div>
                  </div>
                  <div style={{ display: 'grid', gap: '8px', justifyItems: 'end' }}>
                    <input
                      type="number"
                      min="1"
                      className="form-input"
                      value={item.quantity || 1}
                      onChange={(e) => {
                        const next = updateCartQuantity(item.id, e.target.value);
                        setItems(next);
                      }}
                      style={{ width: '86px' }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => {
                        const next = removeFromCart(item.id);
                        setItems(next);
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}

              {hasPhysicalProducts && (
                <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: '10px', display: 'grid', gap: '10px' }}>
                  <div style={{ fontWeight: 600 }}>Physical Book Delivery Details</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className={`btn btn-sm ${deliveryOption === 'collection' ? 'btn-gold' : 'btn-outline'}`}
                      onClick={() => setDeliveryOption('collection')}
                    >
                      Collection (R0)
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${deliveryOption === 'delivery' ? 'btn-gold' : 'btn-outline'}`}
                      onClick={() => setDeliveryOption('delivery')}
                    >
                      Delivery (R{DELIVERY_AMOUNT})
                    </button>
                  </div>
                  <input
                    className="form-input"
                    placeholder="Full name"
                    value={shippingAddress.fullName}
                    onChange={(e) => updateShippingAddress('fullName', e.target.value)}
                  />
                  <input
                    className="form-input"
                    placeholder="Phone number"
                    value={shippingAddress.phone}
                    onChange={(e) => updateShippingAddress('phone', e.target.value)}
                  />
                  <input
                    className="form-input"
                    placeholder="Address line"
                    value={shippingAddress.addressLine1}
                    onChange={(e) => updateShippingAddress('addressLine1', e.target.value)}
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px' }}>
                    <input
                      className="form-input"
                      placeholder="City"
                      value={shippingAddress.city}
                      onChange={(e) => updateShippingAddress('city', e.target.value)}
                    />
                    <input
                      className="form-input"
                      placeholder="Postal code"
                      value={shippingAddress.postalCode}
                      onChange={(e) => updateShippingAddress('postalCode', e.target.value)}
                    />
                    <input
                      className="form-input"
                      placeholder="Country"
                      value={shippingAddress.country}
                      onChange={(e) => updateShippingAddress('country', e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gap: '6px', paddingTop: '8px', borderTop: '1px solid var(--gray-100)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div style={{ color: 'var(--gray-600)' }}>Subtotal</div>
                  <div>R{subtotal.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div style={{ color: 'var(--gray-600)' }}>Delivery</div>
                  <div>R{deliveryFee.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                  <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>Total</div>
                  <div style={{ fontWeight: 800, fontSize: '1.2rem' }}>
                    R{total.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              {error && <div className="badge badge-danger">{error}</div>}

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-gold" onClick={handleCheckout} disabled={checkoutLoading}>
                  {checkoutLoading ? 'Redirecting...' : 'Checkout with Payfast'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    clearCart();
                    setItems([]);
                  }}
                >
                  Clear Cart
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
